import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography
} from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import { addInquiry } from 'src/ApiServices';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const AddInquiryModal = ({ open, onClose, inquiry, onSuccess }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_no: '',
    date: '',
    time: '',
    comment: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const inquiryData = {
        name: formData.name,
        email: formData.email,
        contact_no: formData.contact_no,
        date: formData.date,
        time: formData.time,
        comment: formData.comment,
        type: inquiry?.type?.id ? inquiry.type.id : 3
      };

      const response = await addInquiry(inquiryData);

      console.log('API Response:', response);
      let successMessage = 'Inquiry added successfully!';

      if (response && response.data) {
        successMessage = response.data.message || successMessage;
        if (response.data.id) {
          // successMessage += ` (ID: ${response.data.id})`;
          successMessage += ``;
        }
      }

      toast.success(successMessage, {
        position: 'top-right',
        autoClose: 3000
      });

      // Clear form data
      setFormData({
        name: '',
        email: '',
        contact_no: '',
        date: '',
        time: '',
        comment: ''
      });

      // Refresh data and close modal
      onSuccess();
    } catch (error) {
      console.error('Failed to add inquiry:', error);

      let errorMessage = 'Failed to add inquiry. Please try again.';

      if (error.response) {
        console.log('Error Response Data:', error.response.data);
        console.log('Error Status:', error.response.status);

        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }

        switch (error.response.status) {
          case 400:
            errorMessage = errorMessage || 'Bad request. Please check your input.';
            break;
          case 401:
            errorMessage = errorMessage || 'Unauthorized. Please login again.';
            break;
          case 403:
            errorMessage = errorMessage || 'Forbidden. You do not have permission.';
            break;
          case 404:
            errorMessage = errorMessage || 'Resource not found.';
            break;
          case 409:
            errorMessage = errorMessage || 'Conflict. This inquiry already exists.';
            break;
          case 422:
            errorMessage = errorMessage || 'Validation error. Please check your inputs.';
            break;
          case 500:
            errorMessage = errorMessage || 'Server error. Please try again later.';
            break;
          default:
            errorMessage = errorMessage || `Error ${error.response.status}: Failed to add inquiry.`;
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        errorMessage = 'No response from server. Please check your network connection.';
      } else {
        console.error('Error:', error.message);
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Clear form when modal is closed
    setFormData({
      name: '',
      email: '',
      contact_no: '',
      date: '',
      time: '',
      comment: ''
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: { xs: 2, sm: 3 },
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1400px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: 'white',
          py: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            <Typography variant="h6" fontFamily="poppinsSemiBold">
              Add New Inquiry
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              minWidth: 'auto',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contact_no"
                value={formData?.contact_no}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                label="Time"
                name="time"
                type="time"
                value={formData.time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments/Requirements"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ fontFamily: 'poppinsMedium' }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              fontFamily: 'poppinsMedium',
              backgroundColor: palette.primary.main,
              '&:hover': {
                backgroundColor: palette.primary.dark
              }
            }}
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Inquiry'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddInquiryModal;
